import type {CallMode} from '@pexip/infinity';
import type {UserAgents} from '@pexip/media-components';

/** Safari versions on macOS where H264 calls fail. */
const H264_BROKEN_SAFARI_VERSIONS = [18.4, 18.5];

/**
 * Chooses the video codecs a call may use. H264 is left out where it is broken.
 * Switching calls favour H264 by leaving out VP8 and VP9, and keep VP8 where
 * H264 is broken.
 *
 * @example
 * getAllowedVideoCodecs({
 *     callMode: 'switching',
 *     browserName: 'Chrome',
 *     browserVersion: 141,
 *     allowVP9: true,
 * }); // {allowH264: true, allowVP8: false, allowVP9: false}
 */
export const getAllowedVideoCodecs = ({
    callMode,
    browserName,
    browserVersion,
    allowVP9,
}: {
    callMode: CallMode;
    browserName: UserAgents | undefined;
    browserVersion: number;
    allowVP9: boolean;
}) => {
    const isH264Broken =
        browserName === 'Safari Mac' &&
        H264_BROKEN_SAFARI_VERSIONS.includes(browserVersion);
    const isSwitching = callMode === 'switching';
    return {
        allowH264: !isH264Broken,
        allowVP8: !isSwitching || isH264Broken,
        allowVP9: allowVP9 && !isSwitching,
    };
};
